import React, { useEffect, useMemo, useRef, useState } from "react";
import SearchComponent from "../../components/Search";
import { Button } from "../../components/Button";
import { ReactComponent as ArrowDownIcon } from "../../assets/icons/arrow-down.svg";
import UserIcon from "../../assets/icons/profile-icon.svg";
import SelectCreator from "../content/SelectCreator";
import { PlaylistForm } from "./components/PlaylistForm";
import { ConfirmDelete } from "./components/Confirmations";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import UIPopover from "../../components/Popover";
import { ReactComponent as DotsIcon } from "../../assets/icons/more.svg";
import clsx from "clsx";
import TableSkeleton from "./components/TableSkeleton";
import { api } from "../../helpers/api";
import { Loader } from "../../components/Loader";
import { PlayListDetail } from "./components/PlaylistDetail";
import { ReactComponent as ArrowLeftIcon } from "../../assets/icons/chevron-left.svg";

type PublisherType = {
  publisher_id: string;
  full_name: string;
  user_name: string;
  email_id: string;
  user_image: string | null;
  mobile: string;
};

type VideoType = {
  video_id: string;
  video_title: string;
  video_description: string | null;
  duration: number;
  short_video: string;
  short_video_hls: string;
  trailer_thumb_image: string;
  is_landscape: number;
  thumb_image: string;
};

type PlaylistType = {
  publisher: {
    full_name: string;
    email_id: string;
  };
  playlist_id: string;
  playlist_title: string;
  playlist_description: string;
  playlist_image: string;
  publisher_id: string;
  videos: VideoType[];
};

const columnHelper = createColumnHelper<PlaylistType>();

function Playlist() {
  const [sortBy, setSortBy] = useState<"title" | undefined>();
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC" | undefined>();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [isUserDetailsOpen, setIsUserDetailsOpen] = React.useState<any>(false);
  const [selectedCourse, setSelectedCourse] = React.useState<any | null>(null);
  const [search, setSearch] = React.useState("");
  const [tableData, setTableData] = React.useState<PlaylistType[]>([]);
  const [isPublisher, setIsPublisher] = React.useState(false);
  const loadMoreRef = useRef<any>(null);
  const [publisher, setPublisher] = useState<null | PublisherType>(null);
  const [filters, setFilters] = useState<{
    date: { startDate?: string; endDate?: string };
    types?: string[];
    selectedDateFilter?: string;
  }>();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isPlaylistFormOpen, setIsPlaylistFormOpen] = useState(false);
  const queryClient = useQueryClient();
  const [selectedPlaylist, setSelectedPlaylist] = useState<any | null>(null);

  useEffect(() => {
    const storedPublisher = localStorage.getItem("selectedPublisher");
    if (storedPublisher) {
      setPublisher(JSON.parse(storedPublisher));
    } else {
      localStorage.setItem(
        "selectedPublisher",
        JSON.stringify({
          publisher_id: "5cce949b-d547-4929-a894-f172f647757f",
          full_name: "EasyEd",
          user_name: "EasyEd Admin",
          email_id: "admin@yopmail.com",
          user_image: "",
          mobile: "",
          _user_id: "",
        })
      );
    }
  }, [isPublisher]);

  const handleClose = () => {
    setIsUserDetailsOpen(false);
    setSelectedCourse(null);
  };

  const fetchContents = async ({ pageParam }) => {
    const res = await api.getPlaylist({
      page: pageParam,
      limit: 100,
      keyword: search,
    });
    return res;
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["playlist", search, filters, sortBy, sortOrder],
    queryFn: fetchContents,
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages, pageParam) => {
      if (lastPage?.data?.length < 10) return null;
      return pageParam + 1;
    },
  });

  const columns = [
    columnHelper.accessor("playlist_title", {
      header: ({ column }) => {
        return (
          <button
            onClick={() => {
              if (sortBy === "title") {
                setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC");
              } else {
                setSortBy("title");
                setSortOrder("ASC");
              }
            }}
            className="p-0 flex items-center gap-[2px]"
          >
            Playlist name
            {sortBy === "title" ? (
              sortOrder === "ASC" ? (
                <ArrowDownIcon className="w-4 h-4" />
              ) : (
                <ArrowDownIcon className="w-4 h-4 rotate-[180deg]" />
              )
            ) : null}
          </button>
        );
      },
      cell: (info) => (
        <div className="flex flex-row items-center gap-3">
          <div className="relative">
            <img
              src={info.row.original.playlist_image || UserIcon}
              alt="playlist"
              className="w-16 h-16 rounded-lg object-cover"
            />
            <div className="absolute bottom-1 right-1 bg-black/70 text-white text-xs px-2 py-1 rounded">
              {info.row.original.videos?.length || 0} videos
            </div>
          </div>
          <div className="flex flex-1 flex-col gap-1">
            <span className="text-base font-medium text-gray-900">
              {info.row.original.playlist_title}
            </span>
            <span className="text-sm text-gray-500 line-clamp-2">
              {info.row.original.playlist_description}
            </span>
          </div>
        </div>
      ),
    }),
    columnHelper.accessor((row) => row?.videos?.length, {
      header: "No of videos",
    }),
    columnHelper.accessor("publisher_id", {
      header: "Created by",
      cell: (info) => (
        <div className="flex flex-row justify-between w-full ">
          <div className="flex flex-row gap-2 items-center">
            <img
              src={UserIcon}
              alt="creator"
              className="w-[35px] h-[35px] rounded object-fill"
            />
            <span className="text-black text-[13px] font-medium text-left ">
              {info.row.original.publisher.full_name}
            </span>
          </div>
          <UIPopover
            trigger={
              <Button
                variant="outline"
                className="hover:!border-[#9D9D9D] hover:!bg-transparent !border-[#D7D7D7] !rounded-md h-[2.5rem] w-[2.5rem] flex !p-2 items-center justify-center [&_circle]:fill-black"
              >
                <DotsIcon />
              </Button>
            }
          >
            {(close) => (
              <div className="flex flex-col gap-4 bg-white p-4 !pr-8 rounded-lg shadow-[0px_6px_26px_0px_rgba(28,43,40,0.14)] font-inter mr-12 mt-1 text-sm">
                <button
                  className="text-start"
                  onClick={() => {
                    setSelectedPlaylist(info.row.original);
                    setIsUserDetailsOpen(true);
                    close();
                  }}
                >
                  View
                </button>
                <button
                  className="text-[#F54343] text-start"
                  onClick={() => {
                    setIsDeleteModalOpen(true);
                    setSelectedCourse(info.row.original);
                    close();
                  }}
                >
                  Delete
                </button>
              </div>
            )}
          </UIPopover>
        </div>
      ),
    }),
  ];

  const table = useReactTable<PlaylistType>({
    data: tableData || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  useMemo(() => {
    if (data) {
      const newData = data.pages.flatMap((page) => page?.data || []);
      setTableData(newData);
    }
  }, [data]);

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["playlist"] });
    refetch();
  }, [isPlaylistFormOpen]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        root: null,
        rootMargin: "20px",
        threshold: 1.0,
      }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const hasFilters = useMemo(() => {
    return filters?.date.startDate || (filters?.types?.length || 0) > 0;
  }, [filters]);

  const Header = () => {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="flex flex-col gap-4">
          <div className="flex flex-row justify-between items-center">
            <div className="flex flex-col gap-1">
              <h1 className="text-2xl font-bold text-gray-900">
                Manage Playlists
              </h1>
              <p className="text-sm text-gray-500">
                Create and manage your video playlists
              </p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsPublisher(!isPublisher)}
                className="flex items-center gap-2 text-gray-800 rounded-lg hover:bg-gray-100 focus:outline-none mr-10"
              >
                {publisher && (
                  <>
                    <img
                      src={
                        publisher.user_image ||
                        UserIcon
                      }
                      alt={publisher.full_name}
                      className="w-12 h-12 rounded-full"
                    />
                    <div>
                      <p className="text-sm font-medium">
                        Creator : {publisher.full_name}
                      </p>
                      <p className="text-sm text-gray-600">
                        {publisher.email_id}
                      </p>
                    </div>
                    <ArrowLeftIcon className="w-4 h-4 [&_path]:stroke-primary" />
                  </>
                )}
              </button>
              <Button
                onClick={() => setIsPlaylistFormOpen(true)}
                variant="primary"
                className="!rounded-full"
              >
                + Create Playlist
              </Button>
            </div>
          </div>
          <div className="w-64">
            <SearchComponent
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              placeholder="Search playlists..."
              value={search}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="flex flex-col gap-4">
        <Header />

        <div
          className={`overflow-auto ${
            hasFilters ? "h-[calc(100vh-260px)]" : "h-[calc(100vh-190px)]"
          } relative flex flex-col`}
        >
          <table className="w-full relative">
            <colgroup>
              <col style={{ width: "60%" }} />
              <col style={{ width: "20%" }} />
              <col style={{ width: "20%" }} />
            </colgroup>
            <thead className="w-full">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="sticky top-0 z-[1]">
                  {headerGroup.headers.map((header, index) => (
                    <th
                      key={header.id}
                      className={clsx(
                        "text-[0.875rem] font-medium text-[#202020] text-start p-0"
                      )}
                    >
                      <div
                        className={clsx(
                          "w-full px-4 py-4 bg-[#F2F7F3] flex flex-row items-center gap-1 transition-all",
                          index === headerGroup?.headers?.length - 1 &&
                            "rounded-tr-[14px] rounded-br-[14px]",
                          index === 0 && "rounded-tl-[14px] rounded-bl-[14px]"
                        )}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {!isLoading &&
                table.getRowModel().rows.map((row, index) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className={clsx(
                          "px-4 py-4 border-b text-black/80 border-b-[#D7D7D7] text-[13px]",
                          index === table?.getRowModel()?.rows?.length - 1 &&
                            "border-b-0",
                          cell.column.id === "publisher_id"
                            ? "text-right"
                            : "text-left"
                        )}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}

              {isFetching && (
                <TableSkeleton
                  rows={5}
                  columns={table?.getAllColumns()?.length}
                />
              )}
            </tbody>
          </table>
          {!isLoading && !isFetching && tableData?.length === 0 && (
            <div className="flex-1 flex justify-center items-center">
              <span className="text-[#999999] text-[0.875rem] font-medium">
                No data available
              </span>
            </div>
          )}
          <div className="">
            <button
              onClick={() => fetchNextPage()}
              disabled={!hasNextPage || isFetchingNextPage}
              ref={loadMoreRef}
            >
              {isFetchingNextPage && (
                <div className="flex justify-center items-center py-4">
                  <Loader size="small" className="text-primary" />
                </div>
              )}
            </button>
          </div>
        </div>
        {isPlaylistFormOpen && (
          <PlaylistForm onClose={() => setIsPlaylistFormOpen(false)} />
        )}
        {isDeleteModalOpen && selectedCourse && (
          <ConfirmDelete
            onClose={() => setIsDeleteModalOpen(false)}
            userId={selectedCourse.playlist_id}
            refetch={() => {
              queryClient.invalidateQueries({ queryKey: ["playlist"] });
              refetch();
            }}
            name={selectedCourse.playlist_title}
          />
        )}
        {!!isUserDetailsOpen && (
          <PlayListDetail
            playlist={selectedPlaylist}
            isOpen={!!isUserDetailsOpen}
            onClose={handleClose}
            refresh={refetch}
          />
        )}
        {isPublisher && (
          <SelectCreator
            isOpen={isPublisher}
            handleClose={() => setIsPublisher(false)}
          />
        )}
      </div>
    </>
  );
}

export default Playlist;
